import { Link } from "react-router-dom";

export default function ContentSection() {
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <article className="prose-custom">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
            Why Every Smart Shopper Needs a <strong className="text-primary-600">CSSBuy Spreadsheet</strong>
          </h2>

          <div className="space-y-6 text-foreground-700 leading-relaxed">
            <p className="text-base">
              In the fast-moving world of agent-based shopping, staying organized is the difference between scoring amazing deals and missing out entirely. A well-maintained <strong>CSSBuy Spreadsheet</strong> serves as your personal command center — consolidating product links, size references, quality ratings, and seller notes into a single, easy-to-navigate document that saves you hours of searching and scrolling.
            </p>

            <p className="text-base">
              The beauty of a <strong>CSSBuy Spreadsheet</strong> lies in its simplicity and power. Rather than juggling dozens of browser tabs and scattered bookmarks, you can browse through carefully organized categories — from <strong>sneakers</strong> and <strong>hoodies</strong> to <strong>jackets</strong> and <strong>accessories</strong> — with detailed notes that help you make informed purchasing decisions. Community members contribute their real experiences, ensuring the information stays accurate and valuable.
            </p>

            <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-4">
                What Makes a <strong className="text-primary-600">CSSBuy Spreadsheet</strong> Different?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                    <i className="ri-check-double-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-label text-sm font-semibold text-foreground-900 mb-1">Community Verified</h4>
                    <p className="text-sm text-foreground-600">Every listing is tested and reviewed by real buyers before being added to the collection.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                    <i className="ri-refresh-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-label text-sm font-semibold text-foreground-900 mb-1">Weekly Updates</h4>
                    <p className="text-sm text-foreground-600">Dead links are removed and fresh finds are added every week to keep everything current.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                    <i className="ri-guide-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-label text-sm font-semibold text-foreground-900 mb-1">Size Guidance</h4>
                    <p className="text-sm text-foreground-600">Detailed sizing notes and fit recommendations help you avoid costly ordering mistakes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                    <i className="ri-price-tag-3-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-label text-sm font-semibold text-foreground-900 mb-1">Price Tracking</h4>
                    <p className="text-sm text-foreground-600">Compare prices across listings and track the best deals with regularly updated pricing data.</p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-8 mb-4">
              Getting Started with Your First <strong className="text-primary-600">CSSBuy Spreadsheet</strong>
            </h3>

            <p className="text-base">
              Whether you are hunting for the latest sneaker releases, building a seasonal wardrobe, or looking for unique accessories, a <strong>CSSBuy Spreadsheet</strong> gives you a massive head start. New users often find that what would normally take weeks of research can be accomplished in a single afternoon — simply by following the organized links and reading community notes. The spreadsheet format makes comparison shopping effortless, letting you evaluate multiple items side by side before committing to a purchase.
            </p>

            <p className="text-base">
              As the <strong>CSSBuy Spreadsheet</strong> community continues to grow, the quality and depth of available collections keep improving. Contributors from around the world share their discoveries, creating a rich ecosystem of verified finds across every imaginable category. The weekly update cycle ensures you never waste time on outdated links or unavailable products — a common frustration with static shopping lists that quickly become obsolete.
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-label text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
              Visit CSSBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-label text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line w-4 h-4 flex items-center justify-center"></i>
              Buy It Now
            </a>
          </div>

          <div className="mt-10 border-t border-background-200/70 pt-8">
            <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-4 text-center">
              Dive Deeper with Our <strong className="text-primary-600">CSSBuy Spreadsheet</strong> Guides
            </h4>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { label: "Complete Guide", to: "/cssbuy-spreadsheet-guide" },
                { label: "Buying Guide", to: "/cssbuy-spreadsheet-buying-guide" },
                { label: "Shipping Guide", to: "/cssbuy-spreadsheet-shipping-guide" },
                { label: "QC Guide", to: "/cssbuy-spreadsheet-qc-guide" },
                { label: "Brand Guide", to: "/cssbuy-spreadsheet-brands" },
                { label: "Safety Guide", to: "/cssbuy-spreadsheet-safety" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 rounded-full text-sm text-foreground-700 hover:text-primary-600 font-medium transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}